import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardHeader,
  InputLabel,
  Select,
  Button,
  Grid,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateData } from "../redux/stats";
import regionsData from "../data/regions.json";

const FilterContent = ({ title }) => {
  // Selected values
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedCrop, setSelectedCrop] = useState("");
  
  const dispatch = useDispatch();

  // Memoized list of unique countries from GeoJSON
  const countries = useMemo(() => {
    const uniqueCountries = new Set(
      regionsData.features.map(feature => feature.properties.Country)
    );
    return Array.from(uniqueCountries).sort();
  }, []);

  // Memoized list of regions filtered by selected country
  const regions = useMemo(() => {
    if (!selectedCountry) return [];
    return regionsData.features
      .filter(feature => feature.properties.Country === selectedCountry)
      .map(feature => feature.properties.LAU_NAME)
      .sort();
  }, [selectedCountry]);

  // Memoized list of crop types filtered by selected country & region
  const cropTypes = useMemo(() => {
    if (!selectedRegion) return [];
    const crops = regionsData.features
      .filter(feature => feature.properties.LAU_NAME === selectedRegion)
      .flatMap(feature => feature.properties.Croptype || []);
    return [...new Set(crops)].sort();
  }, [selectedRegion]);

  // console.log("Crops:", cropTypes);
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedRegion("");
    setSelectedCrop("");
  };

  const handleSearch = () => {
    // Only search if both country and region are selected
    if (!selectedCountry || !selectedRegion) {
      console.log("Missing selection:", { selectedCountry, selectedRegion });
      return;
    }

    const selectedFeature = regionsData.features.find(
      feature => 
        feature.properties.Country === selectedCountry && 
        feature.properties.LAU_NAME === selectedRegion
    );
    
    // console.log("Selected Feature:", selectedFeature);
    
    if (selectedFeature) {
      const updatePayload = {
        ...selectedFeature.properties,
        cropType: selectedCrop,
        Country: selectedCountry,
        LAU_NAME: selectedRegion
      };
      console.log("Dispatching update with:", updatePayload);
      dispatch(updateData(updatePayload));
    }
  };

  return (
    <Card>
      <CardHeader
        title={title}
        sx={{
          position: "relative",
          bgcolor: "#DBD7D2",
          height: "3em",
        }}
      />
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Country</InputLabel>
              <Select
                value={selectedCountry}
                label="Country"
                onChange={(e) => handleCountryChange(e.target.value)}
              >
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6}>
            <FormControl 
              fullWidth 
              size="small" 
              sx={{ mb: 2 }}
            >
              <InputLabel>Region</InputLabel>
              <Select
                value={selectedRegion}
                label="Region"
                onChange={(e) => setSelectedRegion(e.target.value)}
                disabled={!selectedCountry}
                sx={{
                  "&.Mui-disabled": {
                    cursor: "not-allowed !important",
                    "& .MuiSelect-select": {
                      cursor: "not-allowed !important"
                    }
                  }
                }}
              >
                {regions.map((region) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={8}>
            <FormControl 
              fullWidth 
              size="small" 
              sx={{ mb: 2 }}
            >
              <InputLabel>Crop</InputLabel>
              <Select
                value={selectedCrop}
                label="Crop"
                onChange={(e) => setSelectedCrop(e.target.value)}
                disabled={!selectedRegion}
                sx={{
                  "&.Mui-disabled": {
                    cursor: "not-allowed !important",
                    "& .MuiSelect-select": {
                      cursor: "not-allowed !important"
                    }
                  }
                }}
              >
                {cropTypes.map((crop) => (
                  <MenuItem key={crop} value={crop}>
                    {crop}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <Button 
              variant="contained" 
              onClick={handleSearch}
              disabled={!selectedCountry || !selectedRegion || !selectedCrop}
              fullWidth
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default FilterContent;